import React from 'react';

/**
 * feat-252 — read-only `Created` / `Updated` metadata line for the six curriculum
 * detail panels in the Learn Module editor (`/quiz-management`).
 *
 * Design authority: `design/timestamps-NOTES.md` (feat-252). One component, six
 * call sites — six hand-rolled copies is how six surfaces drift.
 *
 * NOT to be confused with `timeAgo()` in `QuizContentManagement.tsx`, which
 * renders the History drawer's *relative* times. That drawer is an event feed
 * where recency ordering is the point; a detail panel answers "when was this
 * authored", which the reader answers against calendar knowledge. Deliberately
 * different formats — do not unify them (NOTES §7).
 */

/** NOTES §5 / AC-F5. Absolute local time, month-short, no seconds, no tz suffix. */
const TIMESTAMP_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

/**
 * Format one ISO-8601 UTC string (`...Z`, the shape `_iso_utc()` emits) into the
 * viewer's local time, or `null` when there is nothing renderable.
 *
 * The value is parsed by `Date`, which honours the `Z` suffix — so a timestamp
 * whose UTC and local calendar days differ renders the LOCAL day. Dropping that
 * UTC handling is the mutation AC-F6(c) controls for.
 *
 * Exported for the unit tests, which assert against a value computed with the
 * same options rather than a hand-typed string (AC-F5).
 */
export function formatContentTimestamp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toLocaleString(undefined, TIMESTAMP_FORMAT);
}

export interface ContentTimestampsProps {
  /**
   * `inline` — rides an identity row that has a free trailing edge, right-aligned,
   * costing zero vertical pixels (Lesson, Drill).
   * `stacked` — drops beneath the panel's title/kind header, left-aligned, when
   * that row's trailing edge is already taken by a control (Question, Rehearsal,
   * Video, Reading). Every surface that can show two lines is `stacked`, so the
   * two-line case never has to fit on a fixed-height row (NOTES §3).
   */
  variant: 'inline' | 'stacked';
  /** ISO-8601 UTC. `undefined`/`null`/unparseable ⇒ renders nothing (AC-F3). */
  created?: string | null;
  /**
   * ISO-8601 UTC. Only the three entity kinds whose table carries an `updated`
   * column ever pass this. `inline` ignores it — those surfaces have no `updated`.
   */
  updated?: string | null;
  /** Extra classes on the root (Video passes `px-1`; callers set their own margin). */
  className?: string;
}

/**
 * Renders nothing at all — `null`, never an empty element — when there is no
 * timestamp to show. Every one of these panels sits in a `space-y-*` parent,
 * where an empty `<div>` still consumes a gap and leaves a visible hole. That is
 * AC-F3's real failure mode, and it is why the tests assert the DOM rather than
 * only the absence of the text (NOTES §4).
 */
export default function ContentTimestamps({
  variant,
  created,
  updated,
  className,
}: ContentTimestampsProps) {
  const createdText = formatContentTimestamp(created);

  // AC-F4 (r2) / NOTES §5: suppression is by RENDERED string, not raw ISO
  // equality. Two values seconds apart are different raw values that format to
  // the identical display string under these options, and rendering
  // `Created Aug 14, 2026, 3:42 PM` directly above `Updated Aug 14, 2026, 3:42 PM`
  // reads as a bug. Raw-equal implies format-equal, so this is strictly stronger
  // than raw comparison.
  const updatedText = variant === 'stacked' ? formatContentTimestamp(updated) : null;
  // `Updated` never renders on its own: with no `Created` there is nothing for it
  // to differ FROM, and the design specifies only the one-line and two-line
  // Created-first cases (NOTES §2).
  const showUpdated = createdText !== null && updatedText !== null && updatedText !== createdText;

  if (createdText === null) return null;

  if (variant === 'inline') {
    return (
      <span
        className={`ml-auto shrink-0 whitespace-nowrap text-xs text-inkMuted${
          className ? ` ${className}` : ''
        }`}
        data-testid="content-timestamps"
      >
        <span className="font-semibold">Created</span> {createdText}
      </span>
    );
  }

  return (
    <div
      className={`space-y-0.5 text-xs text-inkMuted${className ? ` ${className}` : ''}`}
      data-testid="content-timestamps"
    >
      <div>
        <span className="font-semibold">Created</span> {createdText}
      </div>
      {showUpdated && (
        <div>
          <span className="font-semibold">Updated</span> {updatedText}
        </div>
      )}
    </div>
  );
}
